import { ConfigType } from "../utils/requestAPI";

export const UserRoles = {
  LANDLORD: "LANDLORD",
  TENANT: "TENANT",
  SERVICE_PROVIDER: "SERVICE_PROVIDER",
} as const;

export type UserRoleType = (typeof UserRoles)[keyof typeof UserRoles];
export type UserSubRole = "HOME_SEEKER" | "STUDENT";

export type paginationType = {
  limit?: number;
  total?: number;
  totalPages?: number;
  currentPage?: number;
  page?: number;
  search?: string;
  roles?: string;
  filter?: string;
  startDate?: string;
  endDate?: string;
};

export type updateData = {
  token?: string;
  email?: string;
  password: string;
  confirmPassword?: string;
};
export type resetData = {
  email: string;
  password: string;
  code?: string;
};

const users = {
  getUsers: (params?: paginationType): ConfigType => {
    return {
      method: "get",
      url: "users",
      params,
    };
  },
  createUser: (data: {
    firstName: string;
    lastName: string;
    address: string;
    email: string;
    phone: string;
    accountStatus?: string;
    role?: string;
    roles?: string[];
    avatar: any;
  }): ConfigType => {
    return {
      method: "post",
      url: "auth/user",
      data,
    };
  },
  updateUser: (
    data: {
      firstName: string;
      lastName: string;
      address: string;
      email: string;
      phone: string;
      accountStatus?: string;
      role: string | string[];
      avatar: any;
    },
    id: string
  ): ConfigType => {
    return {
      method: "patch",
      url: `admins/users/${id}`,
      data,
    };
  },
  updateAdmin: (
    data: {
      firstName?: string;
      lastName?: string;
      address?: string;
      email?: string;
      phone?: string;
      accountStatus?: string;
      role?: string | string[];
      avatar?: any;
    },
    id: string
  ): ConfigType => {
    return {
      method: "patch",
      url: `admins/`,
      data,
    };
  },
  getAdmins: (params?: paginationType): ConfigType => {
    return {
      method: "get",
      url: "admins",
      params,
    };
  },

  getSingleAdmin: (id: string): ConfigType => {
    return {
      method: "get",
      url: `admins/${id}`,
    };
  },

  deleteAdmin: (id: string): ConfigType => {
    return {
      method: "delete",
      url: `admins/${id}`,
    };
  },

  deleteUser: (id: string): ConfigType => {
    return {
      method: "delete",
      url: `admins/user/${id}`,
    };
  },

  login: (data: { email: string; password: string }): ConfigType => {
    return {
      method: "post",
      url: "/admins/login",
      data,
    };
  },
  createLandlord: (data: {
    email: string;
    firstName: string;
    lastName: string;
  }): ConfigType => {
    return {
      method: "post",
      url: "/admins/landlord",
      data,
    };
  },

  createAdmin: (data: {
    email: string;
    firstName: string;
    lastName: string;
    avatar?: string;
    role: string | string[];
  }): ConfigType => {
    return {
      method: "post",
      url: "/admins",
      data,
    };
  },

  getUserDetails: (id: string): ConfigType => {
    return {
      method: "get",
      url: `auth/${id}`,
    };
  },

  getAnalytics: (): ConfigType => {
    return {
      method: "get",
      url: "/admins/analytics",
    };
  },
  getGrowthAnalytics: (params?: {
    year: number;
    month: number;
  }): ConfigType => {
    return {
      method: "get",
      url: "admins/analytics/users",
      params,
    };
  },
  getPropertyAnalytics: (params?: {
    year: number;
    month: number;
    status?: boolean;
  }): ConfigType => {
    return {
      method: "get",
      url: "admins/properties/analytics",
      params,
    };
  },
  getUsersTransactions: ({
    userId,
    params,
  }: {
    userId: string;
    params: paginationType;
  }): ConfigType => {
    return {
      method: "get",
      url: `/transactions/users/${userId}`,
      params,
    };
  },

  updatePassword: (data: updateData): ConfigType => ({
    method: "post",
    url: "/landlord/password",
    data,
  }),

  updateAdminPassword: (data: updateData): ConfigType => ({
    method: "post",
    url: "/admins/password",
    data,
  }),
  resetPassword: (data: resetData): ConfigType => ({
    method: "post",
    url: "admins/reset-password",
    data,
  }),

  blockUser: (id: string, data: { role: UserRoleType }): ConfigType => ({
    method: "post",
    url: `/auth/user/${id}/block`,
    data,
  }),
  unblockUser: (id: string, data: { role: UserRoleType }): ConfigType => ({
    method: "post",
    url: `/auth/user/${id}/unblock`,
    data,
  }),
  forgotPassword: (email: string): ConfigType => ({
    method: "post",
    url: `admins/forgot-password`,
    data: { email: email },
  }),
};

export default users;
